import React, { useEffect, useState } from 'react';
import { listDocumentVersions, DocumentVersion } from '../../../api/documentApi';

interface VersionHistoryModalProps {
  documentId: number;
  t: any;
  onClose: () => void;
  // Resolves once the version is in the editor; rejects if it wasn't.
  onRestore: (versionId: number) => Promise<void>;
  /** A viewer may read the history but not put a version back */
  canRestore?: boolean;
}

export const VersionHistoryModal: React.FC<VersionHistoryModalProps> = ({
  documentId,
  t,
  onClose,
  onRestore,
  canRestore = true,
}) => {
  const [versions, setVersions] = useState<DocumentVersion[] | null>(null);
  const [restoringId, setRestoringId] = useState<number | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    listDocumentVersions(documentId)
      .then((data) => {
        if (!cancelled) setVersions(data.versions);
      })
      .catch(() => { if (!cancelled) setVersions([]); });
    return () => { cancelled = true; };
  }, [documentId]);

  const handleRestore = async (versionId: number) => {
    if (!window.confirm(t.editor.restoreConfirm)) return;
    setRestoringId(versionId);
    setError('');
    try {
      await onRestore(versionId);
      onClose();
    } catch {
      setError(t.editor.restoreFailed);
    } finally {
      setRestoringId(null);
    }
  };

  return (
    <div className="word-count-modal-overlay" onClick={onClose}>
      <div className="word-count-modal version-history-modal" onClick={(e) => e.stopPropagation()}>
        <div className="word-count-modal-header">
          <h3>{t.editor.versionHistory}</h3>
          <button type="button" className="word-count-close-btn" onClick={onClose}>✕</button>
        </div>

        <p className="version-history-intro">{t.editor.versionHistoryIntro}</p>
        {error && <p className="version-history-error">{error}</p>}

        {versions === null ? (
          <p className="version-history-empty">{t.editor.versionsLoading}</p>
        ) : versions.length === 0 ? (
          <p className="version-history-empty">{t.editor.noVersionsYet}</p>
        ) : (
          <ul className="version-history-list">
            {versions.map((version) => (
              <li key={version.id} className="version-history-item">
                <div className="version-history-meta">
                  <span className="version-history-time">
                    {new Date(version.created_at).toLocaleString(undefined, {
                      dateStyle: 'medium', timeStyle: 'short',
                    })}
                  </span>
                  <span className="version-history-words">
                    {version.word_count.toLocaleString()} {t.editor.versionWords}
                  </span>
                </div>
                {version.preview && (
                  <div className="version-history-preview">{version.preview}</div>
                )}
                {canRestore && (
                  <button
                    type="button"
                    className="version-history-restore"
                    disabled={restoringId !== null}
                    onClick={() => handleRestore(version.id)}
                  >
                    {restoringId === version.id ? '…' : t.editor.restoreVersion}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
